import { useCallback, useEffect, useRef, useState } from 'react'

import { shuffleArray } from '~/bootcamp/Activities/utils/activities.utils'

import { PRIME_NUMBERS } from '~/bootcamp/Activities/Factorization/constants'

const PRIME_BY_LEVELS: { [key: number]: { primes: number[]; range: [number, number] } } = {
  1: {
    primes: [2, 3, 5, 7],
    range: [2, 10],
  },
  2: {
    primes: [11, 13, 17, 19, 23, 29, 31, 37],
    range: [10, 40],
  },
  3: {
    primes: [41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97],
    range: [40, 99],
  },
}

const generateQuestion = (levelNumber: number, totalOptions: number): { options: number[]; answer: number } => {
  if (!totalOptions) throw new Error(`You should expect atleast 1 option`)

  const primeData = PRIME_BY_LEVELS[levelNumber]
  if (!primeData) throw new Error(`Level ${levelNumber} not configured yet`)

  const { primes, range } = primeData
  const possileOptions = Array.from(Array(range[1] - range[0] + 1).keys())
    .map((n) => n + range[0])
    .filter((n) => !PRIME_NUMBERS.has(n))

  if (totalOptions > possileOptions.length + 1) {
    throw new Error(
      `Expected number of option is ${totalOptions} but possible options are ${possileOptions.length + 1}`,
    )
  }

  const answer = shuffleArray<number>(primes, 1)[0]
  const optionsExcludingPrime = shuffleArray<number>(possileOptions, totalOptions - 1)

  return {
    options: shuffleArray([...optionsExcludingPrime, answer]),
    answer,
  }
}

export const usePrimeSelect = (level = 1, noOfOptions = 4): [number[], () => void, (userAnswer: number) => boolean] => {
  const [options, setOptions] = useState<number[]>([])
  const [answer, setAnswer] = useState<number>(0)
  const taskHistory = useRef<number[]>([])
  const retry = useRef<number>(0)

  const generateNewQuestion = useCallback((): void => {
    const question = generateQuestion(level, noOfOptions)

    if (!taskHistory.current.includes(question.answer) || retry.current >= 3) {
      taskHistory.current = [...taskHistory.current, question.answer].slice(-3)
      setOptions(question.options)
      setAnswer(question.answer)
      retry.current = 0
    } else {
      generateNewQuestion()
      retry.current += 1
    }
  }, [level, noOfOptions])

  useEffect(() => {
    generateNewQuestion()
  }, [generateNewQuestion])

  const isAnswerCorrect = (userAnswer: number): boolean => {
    return answer === userAnswer
  }

  return [options, generateNewQuestion, isAnswerCorrect]
}
